<template>
  <PanelItem :index="index" :field="field">
    <template #value>
      <div class="cf-panel">
        <div class="cf-panel__bar">
          <div class="cf-panel__stats">
            <span>{{ label('total') }}: {{ roster.total || 0 }}</span>
            <span>{{ label('ours') }}: {{ roster.ours || 0 }}</span>
            <span class="cf-panel__stat--bad">{{ label('unknowns') }}: {{ roster.unknown || 0 }}</span>
            <span v-if="roster.fetched_at_label" class="cf-panel__muted">
              {{ label('fetched') }}: {{ roster.fetched_at_label }}
            </span>
          </div>
          <div class="cf-panel__actions">
            <button
              type="button"
              class="cf-panel__btn"
              :disabled="loading"
              @click="refresh"
            >
              {{ loading ? label('refreshing') : label('refresh') }}
            </button>
          </div>
        </div>

        <p v-if="roster.uid_hidden" class="cf-panel__note">{{ label('uid_hidden') }}</p>
        <p v-if="error" class="cf-panel__error">{{ error }}</p>
        <p v-if="flash" class="cf-panel__flash">{{ flash }}</p>

        <p v-if="!roster.fetched && !loading" class="cf-panel__muted">{{ label('empty') }}</p>
        <p v-else-if="roster.fetched && !(roster.rows || []).length && !loading" class="cf-panel__muted">
          {{ label('none') }}
        </p>

        <div v-else-if="(roster.rows || []).length" class="cf-panel__table-wrap">
          <table class="cf-panel__table">
            <thead>
              <tr>
                <th>{{ label('uid') }}</th>
                <th>{{ label('nickname') }}</th>
                <th>{{ label('subscriber') }}</th>
                <th>{{ label('subscription') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in roster.rows"
                :key="(row.uid || row.nickname || i) + '-' + i"
                :class="'cf-panel__row--' + row.status"
              >
                <td class="cf-panel__mono">{{ row.uid || '—' }}</td>
                <td>{{ row.nickname }}</td>
                <td>
                  <template v-if="row.in_bot">{{ row.subscriber_name || row.chat_id }}</template>
                  <template v-else>{{ label('unknown') }}</template>
                </td>
                <td>
                  <span v-if="row.status === 'lead'">{{ label('lead') }}</span>
                  <span v-else-if="row.entitled">
                    {{ daysLabel(row.days_remaining) }}
                    <span v-if="row.ends_at" class="cf-panel__muted"> · {{ row.ends_at }}</span>
                  </span>
                  <span v-else-if="row.in_bot">{{ label('no_sub') }}</span>
                  <span v-else>{{ label('unknown') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </PanelItem>
</template>

<script>
export default {
  props: ['index', 'resourceName', 'resourceId', 'field'],

  data() {
    return {
      roster: { fetched: false, rows: [], total: 0, ours: 0, unknown: 0 },
      loading: false,
      removingAll: false,
      busyUid: null,
      error: '',
      flash: '',
    }
  },

  computed: {
    exchangeId() {
      return this.field.exchange_id || this.resourceId
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    label(key) {
      return (this.field.labels && this.field.labels[key]) || key
    },

    daysLabel(n) {
      if (n === null || n === undefined) {
        return '—'
      }
      return this.label('days').replace(':n', String(n))
    },

    endpoint(suffix = '') {
      return `/nova-api/copy-followers/${this.exchangeId}${suffix}`
    },

    async load() {
      this.error = ''
      try {
        const { data } = await Nova.request().get(this.endpoint())
        this.roster = data
      } catch (e) {
        this.error = e?.response?.data?.message || e.message || 'Error'
      }
    },

    async refresh() {
      this.loading = true
      this.error = ''
      this.flash = ''
      try {
        const { data } = await Nova.request().post(this.endpoint('/refresh'))
        this.roster = data
      } catch (e) {
        this.error = e?.response?.data?.message || e.message || 'Error'
      } finally {
        this.loading = false
      }
    },

    async removeOne(row) {
      if (!row?.uid) {
        return
      }
      this.busyUid = row.uid
      this.error = ''
      this.flash = ''
      try {
        const { data } = await Nova.request().post(this.endpoint('/remove'), { uid: row.uid })
        this.flash = data.message || ''
        if (data.roster) {
          this.roster = data.roster
        }
      } catch (e) {
        this.error = e?.response?.data?.message || e.message || 'Error'
        if (e?.response?.data?.roster) {
          this.roster = e.response.data.roster
        }
      } finally {
        this.busyUid = null
      }
    },

    async removeUnknown() {
      this.removingAll = true
      this.loading = true
      this.error = ''
      this.flash = ''
      try {
        const { data } = await Nova.request().post(this.endpoint('/remove-unknown'))
        this.flash = data.message || ''
        if (data.roster) {
          this.roster = data.roster
        }
      } catch (e) {
        this.error = e?.response?.data?.message || e.message || 'Error'
        if (e?.response?.data?.roster) {
          this.roster = e.response.data.roster
        }
      } finally {
        this.removingAll = false
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.cf-panel {
  width: 100%;
  max-width: 100%;
}
.cf-panel__bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.cf-panel__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
}
.cf-panel__stat--bad {
  color: #e11d48;
}
.cf-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.cf-panel__btn {
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(24, 24, 27, 0.06);
  color: inherit;
}
.cf-panel__btn:disabled {
  opacity: 0.5;
}
.cf-panel__btn--danger {
  background: rgba(225, 29, 72, 0.12);
  color: #be123c;
}
.cf-panel__btn--small {
  padding: 0.25rem 0.7rem;
  font-size: 0.75rem;
}
.cf-panel__note,
.cf-panel__muted {
  font-size: 0.75rem;
  color: #71717a;
  margin: 0.25rem 0 0.75rem;
}
.cf-panel__error {
  color: #e11d48;
  font-size: 0.85rem;
  margin: 0.25rem 0;
}
.cf-panel__flash {
  color: #047857;
  font-size: 0.85rem;
  margin: 0.25rem 0;
}
.cf-panel__table-wrap {
  overflow-x: auto;
}
.cf-panel__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}
.cf-panel__table th {
  text-align: start;
  font-size: 0.7rem;
  font-weight: 700;
  color: #71717a;
  padding: 0.4rem 0.5rem;
  border-bottom: 1px solid rgba(113, 113, 122, 0.25);
}
.cf-panel__table td {
  padding: 0.55rem 0.5rem;
  border-bottom: 1px solid rgba(113, 113, 122, 0.12);
  vertical-align: middle;
}
.cf-panel__mono {
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.cf-panel__cell-action {
  white-space: nowrap;
}
.cf-panel__row--unknown td {
  background: rgba(225, 29, 72, 0.12);
  color: #9f1239;
}
.cf-panel__row--expired td {
  background: rgba(245, 158, 11, 0.12);
}
.cf-panel__row--lead td {
  opacity: 0.75;
}
</style>
